// ─── clinicalThresholds.js ─────────────────────────────────────────────
// V97.65 (migration Supabase éditable) — SEUILS de détection du détecteur de
// profils (_detector.fr.js), sortis en registre nommé DB-éditable.
//
// AVANT : nombres magiques dispersés en dur dans _detector.fr.js (age >= 52,
// stress >= 8, IAH >= 5…). Ces seuils sont marqués DRAFT (« to be reviewed
// with Anissa ») depuis V96.11 et n'ont jamais été relus cliniquement. Cette
// migration les rend ÉDITABLES par Anissa — c'est le PRÉREQUIS technique de sa
// session de revue des seuils (doc V2, point 7). La migration NE CHANGE PAS les
// valeurs, elle change juste où elles vivent.
//
// PORTÉE / RISQUE : ces seuils pilotent la DÉTECTION des modules d'ENRICHISSEMENT
// du composer (femmeCycle, périménopause, néphropathie…), PAS les blocs sécurité
// P0 (clinicalMaternalSafety) ni le blocage de la clairance. Depuis V97.61, le
// blocage rénal de la clairance lit hasDeclaredRenalImpairment (déclaré), pas le
// tag heuristique — donc changer nephropathie_t1_anciennete_min n'affecte QUE
// l'enrichissement, jamais un blocage. Risque faible, données non validées.
//
// FAIL-SAFE : le hardcode reste le fallback. Une valeur DB non-numérique ou une
// clé absente retombe sur le hardcode (getThreshold ne renvoie jamais NaN).
//
// Aucun import — consommable par le détecteur sans risque de cycle.

// ── HARDCODE FALLBACK (valeurs DRAFT actuelles, non validées Anissa) ──
// key → { value, description }. La description accompagne Anissa lors de sa
// revue (studio / futur cockpit). Coupures existantes du code, inchangées.
// V97.66 — valeurs SCELLÉES par Anissa 2026-07-02 (session revue des seuils,
// docs/SEUILS-DETECTEUR-A-VALIDER-ANISSA.md). Changements vs DRAFT :
//   - menopause_age_min 52 → 45 (plancher, pas un âge diagnostique fixe)
//   - digestif : inversion <=2 → >=3 (renommé _max → _min)
//   - burnout_stress_min 8 → 7
//   - femme_cycle_age_max AJOUTÉ 55 (frontière découplée de la ménopause)
//   - nephropathie_t1_anciennete_min SUPPRIMÉ (l'ancienneté du diabète ne
//     suffit plus à suspecter une néphropathie — marqueurs rénaux déclarés
//     uniquement, cf. RE_RENAL_DECLARED dans _detector.fr.js).
export const DETECTOR_THRESHOLDS_META = Object.freeze({
  menopause_age_min: { value: 45, description: 'Âge PLANCHER pour classer ménopause (combiné à aménorrhée ≥ 12 mois). Ce n\'est pas un âge diagnostique fixe : la ménopause repose sur l\'aménorrhée, l\'âge écarte seulement les ménopauses précoces. Validé Anissa 2026-07-02.' },
  perimenopause_age_min: { value: 40, description: 'Âge minimum de la fenêtre périménopause.' },
  perimenopause_age_max: { value: 55, description: 'Âge maximum de la fenêtre périménopause.' },
  perimenopause_signs_min: { value: 2, description: 'Nombre minimum de signes hormonaux (bouffées, sueurs, cycle irrégulier, sommeil) pour classer périménopause dans la fenêtre d\'âge.' },
  femme_cycle_age_min: { value: 18, description: 'Âge minimum du profil femmeCycle (femme en âge de procréer).' },
  femme_cycle_age_max: { value: 55, description: 'Âge maximum du profil femmeCycle (borne haute souple, découplée de la ménopause depuis V97.66). Au-delà sans ménopause confirmée : aucun profil primaire. Validé Anissa 2026-07-02.' },
  digestif_ballonnements_min: { value: 3, description: 'Score de ballonnements (échelle 1-5) au niveau ou AU-DESSUS duquel on ajoute le module digestif chronique (symptomatologie suffisamment présente). Validé Anissa 2026-07-02.' },
  // ⚠️ INACTIF depuis le 2026-08-01 — NE PLUS LE PRÉSENTER COMME UN RÉGLAGE.
  //
  // Ce seuil n'est plus consulté par aucun code de production. Il pilotait une
  // branche du détecteur SAOS qui lisait trois champs n'ayant jamais existé
  // (`apneeSommeilNiveau`, `apneeNiveau`, `iah`) ; cette branche a été
  // supprimée. Le modifier ne change RIEN au comportement.
  //
  // Il n'est conservé que parce qu'un test d'infrastructure du cache s'en sert
  // comme témoin. Anissa, 2026-08-01 : « un seuil clinique inutilisé ne doit
  // pas rester indéfiniment uniquement pour tester une infrastructure
  // technique ». À l'audit final : basculer ce test sur un paramètre neutre,
  // puis archiver ou supprimer ce seuil sur sa décision.
  apnee_iah_min: {
    value: 5,
    inactif: true,
    description: 'INACTIF — non consulté depuis le 2026-08-01. Ancien IAH minimum pour suspicion d\'apnée du sommeil (SAOS). Le modifier n\'a aucun effet.',
  },
  burnout_stress_min: { value: 7, description: 'Niveau de stress déclaré (échelle 1-10) déclenchant le module burn-out / cortisol. Validé Anissa 2026-07-02.' },
});

// Vue plate key → value (fallback direct).
const HARDCODE_VALUES = Object.freeze(
  Object.fromEntries(Object.entries(DETECTOR_THRESHOLDS_META).map(([k, v]) => [k, v.value])),
);

// ── CACHE DB SUPABASE (même pattern que clinicalInteractions.js) ───────
let _dbCache = null; // { key: number } | null
let _dbCacheLoadedAt = 0;
const DB_CACHE_TTL_MS = 5 * 60 * 1000; // 5 min

/**
 * Précharge les seuils depuis Supabase (table clinical_thresholds). À appeler
 * au mount d'un composant qui génère un plan. Idempotent (un fetch par TTL).
 * Fallback silencieux sur hardcode.
 *
 * @param {object} supabaseClient - Client Supabase (from('clinical_thresholds'))
 * @param {object} [opts]
 * @param {boolean} [opts.force=false]
 * @returns {Promise<{ ok: boolean, count?: number, source: 'supabase'|'hardcode', error?: string }>}
 */
export async function preloadThresholdsFromSupabase(supabaseClient, opts = {}) {
  const { force = false } = opts;
  if (!force && _dbCache && (Date.now() - _dbCacheLoadedAt < DB_CACHE_TTL_MS)) {
    return { ok: true, count: Object.keys(_dbCache).length, source: 'supabase' };
  }
  if (!supabaseClient || typeof supabaseClient.from !== 'function') {
    return { ok: false, error: 'no supabase client', source: 'hardcode' };
  }
  try {
    const { data, error } = await supabaseClient
      .from('clinical_thresholds')
      .select('threshold_key, value')
      .eq('enabled', true);
    if (error || !Array.isArray(data) || data.length === 0) {
      return { ok: false, error: error?.message || 'empty table', source: 'hardcode' };
    }
    const map = {};
    for (const row of data) {
      if (!row.threshold_key) continue;
      // Supabase peut renvoyer `numeric` en string : on normalise et on
      // n'accepte QUE des nombres finis (défensif — jamais un NaN en cache).
      const v = Number(row.value);
      if (Number.isFinite(v)) map[row.threshold_key] = v;
    }
    if (Object.keys(map).length === 0) {
      return { ok: false, error: 'no usable row', source: 'hardcode' };
    }
    _dbCache = map;
    _dbCacheLoadedAt = Date.now();
    return { ok: true, count: Object.keys(map).length, source: 'supabase' };
  } catch (e) {
    return { ok: false, error: e?.message || String(e), source: 'hardcode' };
  }
}

function dbFresh() {
  return _dbCache && (Date.now() - _dbCacheLoadedAt < DB_CACHE_TTL_MS);
}

/**
 * Valeur active d'un seuil. DB si fraîche ET clé présente ET numérique ; sinon
 * hardcode. Ne renvoie JAMAIS NaN/undefined pour une clé connue (fail-safe :
 * le fallback par clé garantit qu'un seuil DB corrompu/absent retombe sur la
 * valeur DRAFT).
 *
 * @param {string} key - clé de DETECTOR_THRESHOLDS_META
 * @returns {number}
 */
export function getThreshold(key) {
  if (dbFresh() && Number.isFinite(_dbCache[key])) {
    return _dbCache[key];
  }
  return HARDCODE_VALUES[key];
}

/** Reset le cache (tests). @internal */
export function _resetThresholdsCache() {
  _dbCache = null;
  _dbCacheLoadedAt = 0;
}

/**
 * Source effective (debug/observabilité).
 * @returns {'supabase'|'hardcode'}
 */
export function getThresholdsSource() {
  return dbFresh() ? 'supabase' : 'hardcode';
}

/** Seuil strict du registre scellé MGD, sans fallback JS. */
export function getMgdThreshold(key, manifest) {
  if (!manifest || manifest.ready !== true || manifest.registryKey !== 'mgd_plan_generation'
      || !Array.isArray(manifest.thresholdPolicies)) {
    throw new Error('clinical_policy_not_ready');
  }
  const row = manifest.thresholdPolicies.find((candidate) => candidate?.threshold_key === key);
  const value = Number(row?.value);
  if (!row || row.enabled !== true || !Number.isFinite(value)) {
    throw new Error('clinical_policy_incomplete');
  }
  return value;
}
